<script setup>
import { computed, ref, watch } from 'vue';

import { useAI } from 'dashboard/composables/useAI';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';
import FormattedContent from './Text/FormattedContent.vue';
import { useMessageContext } from '../provider.js';
import { ATTACHMENT_TYPES, MESSAGE_TYPES } from '../constants';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { additionalAttributes, content, id, messageType } = useMessageContext();
const { isAIIntegrationEnabled, getAudioTranscript, getFileTranscript } =
  useAI();

const transcription = ref('');
const isGenerating = ref(false);
const hasRequestedTranscription = ref(false);

const fileType = computed(
  () => props.attachment.fileType ?? props.attachment.file_type
);

const attachmentUrl = computed(
  () => props.attachment.dataUrl ?? props.attachment.data_url ?? ''
);

const cachedTranscript = computed(() => {
  return (
    additionalAttributes.value?.fileTranscript ??
    additionalAttributes.value?.file_transcript ??
    ''
  );
});

const isSupportedAttachment = computed(() => {
  return [
    ATTACHMENT_TYPES.AUDIO,
    ATTACHMENT_TYPES.FILE,
    ATTACHMENT_TYPES.IMAGE,
  ].includes(fileType.value);
});

const shouldRender = computed(() => {
  return (
    messageType.value === MESSAGE_TYPES.INCOMING &&
    isAIIntegrationEnabled.value &&
    isSupportedAttachment.value &&
    !!attachmentUrl.value
  );
});

const generateTranscription = async () => {
  if (!shouldRender.value || hasRequestedTranscription.value) return;

  hasRequestedTranscription.value = true;

  if (cachedTranscript.value) {
    transcription.value = cachedTranscript.value;
    if (fileType.value !== ATTACHMENT_TYPES.AUDIO) return;
  } else if (fileType.value === ATTACHMENT_TYPES.AUDIO && content.value) {
    transcription.value = content.value;
  }

  isGenerating.value = true;
  try {
    const getTranscript =
      fileType.value === ATTACHMENT_TYPES.AUDIO
        ? getAudioTranscript
        : getFileTranscript;
    const result = await getTranscript(attachmentUrl.value, id.value);
    transcription.value = result || '';
  } catch {
    transcription.value = '';
  } finally {
    isGenerating.value = false;
  }
};

watch(shouldRender, generateTranscription, { immediate: true });
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRender"
    class="grid gap-2 rounded-lg border border-n-container bg-n-alpha-1 p-3 text-n-slate-12"
    data-bubble-name="transcription"
  >
    <div class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11">
      <Icon icon="i-lucide-sparkles" class="size-3.5" />
      <span>{{ $t('COMPONENTS.FILE_BUBBLE.TRANSCRIPTION') }}</span>
    </div>
    <div
      v-if="isGenerating"
      class="flex items-center gap-2 text-xs text-n-slate-11"
    >
      <Spinner :size="14" />
      <span>{{ $t('COMPONENTS.FILE_BUBBLE.TRANSCRIPTION_LOADING') }}</span>
    </div>
    <FormattedContent
      v-else-if="transcription"
      :content="transcription"
      class="whitespace-pre-line italic"
    />
    <span v-else class="text-xs italic text-n-slate-11">
      {{ $t('COMPONENTS.FILE_BUBBLE.TRANSCRIPTION_UNAVAILABLE') }}
    </span>
  </div>
</template>
